import { DOCUMENT } from '@angular/common';
import {
  ApplicationRef, ComponentRef, createComponent, EnvironmentInjector, Inject, Injectable, OnDestroy
} from '@angular/core';
import { Store } from '@ngrx/store';
import { default as printJS } from 'print-js';
import {
  firstValueFrom, from, lastValueFrom, map, mergeMap, of, take, timeout, toArray, catchError, Subscription, tap
} from 'rxjs';
import { SubSink } from 'subsink';

import { JobDetailsV2Fragment, JobsV3PageGQL } from '../../../generated/graphql.generated';
import { BrandingService } from '../../services/branding.service';
import { FreyaNotificationsService } from '../../services/freya-notifications.service';

import { BillOfLadingComponent } from './bill-of-lading.component';

export interface PrintBolEventInput {
  eventId: string;
  jobId: string;
}

@Injectable({
  providedIn: 'root'
})
export class PrintBolService implements OnDestroy {

  private subs = new SubSink();

  constructor(
    private jobsV3PageGQL: JobsV3PageGQL,
    private brandingService: BrandingService,
    private appRef: ApplicationRef,
    private injector: EnvironmentInjector,
    private notify: FreyaNotificationsService,
    private store: Store,
    @Inject(DOCUMENT) private document: Document,
  ) { }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  async printBolForJob(jobId: string) {
      this.notify.info('Preparing Bill of Lading...', 'Please wait while we fetch the data.');
      try {
          const job = await firstValueFrom(this.jobsV3PageGQL.fetch({
              jobId,
              resolve: ['discounts', 'users', 'locations', 'distances', 'artifacts', 'charges']
          }).pipe(map(res => res.data?.jobs?.jobs?.[0])));

          if (!job || !job.events) {
              this.notify.info('No events found for this job');
              return;
          }

          const events: PrintBolEventInput[] = job.events.map(e => ({
              eventId: e.id,
              jobId: job.id
          }));

          await this.printEvents(events);

      } catch (error) {
          console.error('Failed to print BOLs for job', error);
          this.notify.error('Failed to print Bill of Lading');
      }
  }

  async printEvents(events: PrintBolEventInput[], onProgress?: (status: string, value: number) => void) {
    if (!events.length) return;

    if (!onProgress) {
        this.notify.info('Preparing Bill of Lading...', 'Please wait while we fetch the data.');
    }

    let wrapper: HTMLElement | null = null;
    let componentRef: ComponentRef<BillOfLadingComponent> | null = null;
    let progressSub: Subscription | null = null;

    const cleanup = () => {
      if (progressSub) {
        progressSub.unsubscribe();
      }
      if (componentRef) {
        this.appRef.detachView(componentRef.hostView);
        componentRef.destroy();
      }
      if (wrapper && wrapper.parentNode) {
        wrapper.remove();
      }
    };

    try {
      const uniqueJobIds = [...new Set(events.map(e => e.jobId))];

      const onFetchProgress = (current: number, total: number) => {
        if (onProgress) {
            const percent = Math.round((current / total) * 50);
            onProgress(`Downloading ${current}/${total}...`, percent);
        } else {
            // Fallback to toasts if no callback provided (legacy call)
            this.notify.clearToasts.next(undefined);
            this.notify.addToast.next({
            severity: 'info',
            summary: 'Downloading data...',
            detail: `${current}/${total} jobs fetched`,
            sticky: true,
            icon: 'pi-cloud-download'
            });
        }
      };

      const jobsMap = await this.fetchJobs(uniqueJobIds, onFetchProgress);

      const printContainerId = 'bol-print-container-' + Date.now();
      
      // Wrapper to hide off-screen without affecting computed styles of children
      wrapper = this.document.createElement('div');
      wrapper.style.position = 'fixed';
      wrapper.style.top = '-10000px';
      wrapper.style.left = '-10000px';
      wrapper.style.zIndex = '-1000';
      this.document.body.appendChild(wrapper);

      const container = this.document.createElement('div');
      container.id = printContainerId;
      // Do NOT set visibility: hidden or position here, as it would be copied by targetStyles: ['*']
      wrapper.appendChild(container);

      // Create a component ref for the BOL container
      componentRef = createComponent<BillOfLadingComponent>(BillOfLadingComponent, {
        environmentInjector: this.injector,
        hostElement: container
      });

      // Prepare data for the component
      // We need to map events to their job data
      const bolData = events.map(eventInput => {
        const job = jobsMap.get(eventInput.jobId);
        const event = job?.events?.find(e => e.id === eventInput.eventId);
        return {
          job,
          event,
        };
      }).filter(d => d.job && d.event);

      // Sort by start time
      bolData.sort((a, b) => (a.event!.start || 0) - (b.event!.start || 0));

      componentRef.instance.bolData = bolData;

      progressSub = componentRef.instance.renderProgress.subscribe((p) => {
        if (onProgress) {
            const percent = 50 + Math.round((p.current / p.total) * 50);
            onProgress(`Generating Document ${p.current}/${p.total}...`, percent);
        } else {
            this.notify.clearToasts.next(undefined);
            this.notify.addToast.next({
            severity: 'info',
            summary: 'Generating documents...',
            detail: `Rendering ${p.current}/${p.total} PDFs`,
            sticky: true,
            icon: 'pi-cog'
            });
        }
      });
      
      // Trigger change detection
      this.appRef.attachView(componentRef.hostView);
      componentRef.changeDetectorRef.detectChanges();

      // Wait for ready event or timeout
      await firstValueFrom(
        componentRef.instance.ready.pipe(
          take(1),
          timeout(30000)
        )
      );

      if (onProgress) {
          onProgress('Printing...', 100);
      } else {
          this.notify.clearToasts.next(undefined);
          this.notify.addToast.next({
            severity: 'success',
            summary: 'Document generated!',
            detail: 'Printing...',
            life: 3000,
            icon: 'pi-print'
          });
      }

      const uniqueJobCodes = Array.from(new Set(bolData.map(d => d.job?.code).filter(Boolean)));
      const title = uniqueJobCodes.length > 0
          ? `Bill of Lading - ${uniqueJobCodes.join(', ')}`
          : 'Bill of Lading';

      await this.printUsingPrintJs(printContainerId, container, title, cleanup);

    } catch (error) {
      console.error('Failed to print BOLs', error);
      this.notify.clearToasts.next(undefined);
      this.notify.error('Failed to print Bill of Lading');
      cleanup();
    }
  }

  private async printUsingPrintJs(_printContainerId: string, container: HTMLElement, title: string, cleanup: () => void) {
    // Pre-convert all external images to inline data URLs.
    // printJS's loadIframeImage polls forever (no timeout) if any image fails to
    // load inside its iframe. By inlining everything we guarantee instant loading.
    await this.inlineAllImages(container);

    // Carry over the current document stylesheets so third-party/global styles
    // that are not part of Angular's injected <style> tags still exist in the
    // print iframe. This is especially important now that we are no longer
    // relying on print-js to scan styles from the live DOM.
    const stylesheetHrefs = Array.from(this.document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'))
      .map(link => link.href)
      .filter(Boolean);

    const angularStyles = Array.from(this.document.querySelectorAll('style'))
      .map(s => s.textContent || '')
      .join('\n');

    const style = angularStyles + `
      @media print {
        body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        .page-break { page-break-after: always; break-after: page; }
        .sheet {
          width: 100% !important;
          min-height: 0 !important;
          margin: 0 !important;
          box-shadow: none !important;
          border: none !important;
        }
        .sheet, .inventory-section, .charges-section, .job-overview,
        .route-section-wrapper, .notes-section, .documents-list-section,
        .signatures-section, .bol-header, .data-table, tr, td, th {
          height: auto !important;
        }
        .data-table thead { display: table-header-group !important; }
        .print-layout-table thead { display: table-row-group !important; }
        tr { page-break-inside: auto !important; }
      }
    `;

    try {
      printJS({
        printable: container,
        type: 'html',
        documentTitle: title,
        css: Array.from(new Set(stylesheetHrefs)),
        scanStyles: false,
        style,
        onPrintDialogClose: () => cleanup(),
        onError: (err) => {
          console.error('printJS failed', err);
          this.notify.error('Print Error', String(err));
          cleanup();
        }
      });
    } catch (err) {
      console.error('printJS threw', err);
      this.notify.error('Print Error', String(err));
      cleanup();
    }
  }

  // Convert every <img> with an external URL to an inline data URL.
  // This prevents printJS's iframe image-polling from hanging forever
  // when a cross-origin image or expired signed URL fails to re-fetch.
  private async inlineAllImages(container: HTMLElement): Promise<void> {
    const imgs = Array.from(container.querySelectorAll('img'));
    if (!imgs.length) return;

    await Promise.all(imgs.map(async (img, idx) => {
      const src = img.getAttribute('src') || '';

      // Already a data URL — nothing to do
      if (src.startsWith('data:')) {
        return;
      }

      if (!src) {
        img.remove();
        return;
      }

      try {
        // Strategy 1: draw the already-loaded image onto a canvas (sync, no refetch)
        if (img.complete && img.naturalWidth > 0) {
          try {
            const dataUrl = this.imgToDataUrl(img);
            img.setAttribute('src', dataUrl);
            return;
          } catch {
            // Canvas tainted by cross-origin — fall through to fetch
          }
        }

        // Strategy 2: fetch the resource as a blob and convert to data URL
        const dataUrl = await this.fetchAsDataUrl(src);
        img.setAttribute('src', dataUrl);
      } catch (err) {
        // If the image was already loaded by the browser (naturalWidth > 0), it is a
        // cross-origin tainted canvas issue — keep the src so the iframe can load it.
        // If the image is still pending, give the element itself a brief chance to
        // resolve before deciding it is broken and removing it.
        if (img.complete && img.naturalWidth > 0) {
          console.warn(`[BOL PRINT]  img[${idx}] could not inline (CORS tainted) — keeping original src`, err);
        } else {
          const resolution = await this.waitForImageResolution(img, 2_000);
          if (resolution === 'loaded' && img.naturalWidth > 0) {
            console.warn(`[BOL PRINT]  img[${idx}] could not inline, but element loaded later — keeping original src`, err);
          } else {
            console.warn(`[BOL PRINT]  img[${idx}] never loaded (broken/missing) — removing`, err);
            img.remove();
          }
        }
      }
    }));
  }

  private waitForImageResolution(
    img: HTMLImageElement,
    timeoutMs: number
  ): Promise<'loaded' | 'error' | 'timeout'> {
    if (img.complete) {
      return Promise.resolve(img.naturalWidth > 0 ? 'loaded' : 'error');
    }

    return new Promise((resolve) => {
      let settled = false;

      const finish = (result: 'loaded' | 'error' | 'timeout') => {
        if (settled) {
          return;
        }
        settled = true;
        clearTimeout(timeoutHandle);
        img.removeEventListener('load', onLoad);
        img.removeEventListener('error', onError);
        resolve(result);
      };

      const onLoad = () => finish('loaded');
      const onError = () => finish('error');
      const timeoutHandle = setTimeout(() => finish('timeout'), timeoutMs);

      img.addEventListener('load', onLoad, { once: true });
      img.addEventListener('error', onError, { once: true });
    });
  }

  // Draw an already-loaded <img> onto a canvas and return a data URL.
  private imgToDataUrl(img: HTMLImageElement): string {
    const canvas = this.document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(img, 0, 0);
    return canvas.toDataURL('image/png');
  }

  // Fetch a URL as a blob and convert to a base-64 data URL.
  private fetchAsDataUrl(url: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      // 10-second timeout per image
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 10_000);

      fetch(url, { mode: 'cors', signal: controller.signal })
        .then(res => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.blob();
        })
        .then(blob => {
          const reader = new FileReader();
          reader.onloadend = () => {
            clearTimeout(timer);
            resolve(reader.result as string);
          };
          reader.onerror = () => {
            clearTimeout(timer);
            reject(reader.error);
          };
          reader.readAsDataURL(blob);
        })
        .catch(err => {
          clearTimeout(timer);
          reject(err);
        });
    });
  }

  private async fetchJobs(
    jobIds: string[],
    onProgress?: (current: number, total: number) => void
  ): Promise<Map<string, JobDetailsV2Fragment>> {
    if (!jobIds.length) return new Map();

    let fetchedCount = 0;
    const concurrency = 5;

    if (onProgress) onProgress(0, jobIds.length);

    const jobs = await lastValueFrom(
      from(jobIds).pipe(
        mergeMap(id =>
          this.jobsV3PageGQL.fetch({
            jobId: id,
            resolve: ['discounts', 'users', 'locations', 'distances', 'artifacts', 'charges']
          }).pipe(
            map(res => res.data?.jobs?.jobs?.[0]),
            tap(() => {
              fetchedCount++;
              if (onProgress) onProgress(fetchedCount, jobIds.length);
            }),
            catchError(error => {
              console.error(`Failed to fetch job ${id}`, error);
              return of(null);
            })
          ),
          concurrency
        ),
        toArray()
      )
    );

    const jobsMap = new Map<string, JobDetailsV2Fragment>();
    jobs.forEach(job => {
      if (job) jobsMap.set(job.id, job);
    });

    return jobsMap;
  }
}
